/**
 * Level identity — the complete set of inputs that determines a level's content.
 *
 * Everything that can change a generated board must appear here, otherwise released
 * levels could silently change under players. See docs/GENERATOR_VERSIONING.md.
 */
export interface LevelIdentity {
  readonly campaignId: string;
  readonly levelNumber: number;
  readonly generatorVersion: number;
  readonly stencilLibraryVersion: number;
  readonly contentVersion: number;
}

export const DEFAULT_CAMPAIGN_ID = 'classic';

/**
 * Bump when the generation algorithm changes in a way that alters output.
 * v2 introduced multi-cell winding arrows; v1 produced single-cell arrows.
 */
export const CURRENT_GENERATOR_VERSION = 3;
/**
 * Bump when stencil masks or the manifest change.
 * v2 added the solid shape family and retired thin silhouettes from selection.
 */
export const CURRENT_STENCIL_LIBRARY_VERSION = 2;
/** Bump for tuning/config changes that intentionally reshape future content. */
export const CURRENT_CONTENT_VERSION = 1;

export const FIRST_LEVEL = 1;
export const MAX_LEVEL = 100000;

export function createLevelIdentity(
  levelNumber: number,
  overrides: Partial<Omit<LevelIdentity, 'levelNumber'>> = {},
): LevelIdentity {
  if (!Number.isInteger(levelNumber) || levelNumber < FIRST_LEVEL || levelNumber > MAX_LEVEL) {
    throw new RangeError(`Level number out of range: ${levelNumber}`);
  }
  return {
    campaignId: overrides.campaignId ?? DEFAULT_CAMPAIGN_ID,
    levelNumber,
    generatorVersion: overrides.generatorVersion ?? CURRENT_GENERATOR_VERSION,
    stencilLibraryVersion: overrides.stencilLibraryVersion ?? CURRENT_STENCIL_LIBRARY_VERSION,
    contentVersion: overrides.contentVersion ?? CURRENT_CONTENT_VERSION,
  };
}

/** Stable key for caches and maps. Fixed field order, no object enumeration. */
export function identityKey(identity: LevelIdentity): string {
  return [
    identity.campaignId,
    identity.generatorVersion,
    identity.stencilLibraryVersion,
    identity.contentVersion,
    identity.levelNumber,
  ].join('/');
}

/**
 * Human readable reproducibility identifier, safe to paste into a bug report.
 * Format: campaignId/generatorVersion/stencilLibraryVersion/contentVersion/levelNumber/rootSeed
 */
export function reproducibilityId(identity: LevelIdentity, rootSeedHex: string): string {
  return `${identityKey(identity)}/${rootSeedHex}`;
}

export function parseReproducibilityId(
  value: string,
): { identity: LevelIdentity; rootSeed: string } | null {
  const parts = value.trim().split('/');
  if (parts.length !== 6) return null;
  const [campaignId, generator, stencil, content, level, rootSeed] = parts;
  const numbers = [generator, stencil, content, level].map((part) => Number.parseInt(part, 10));
  if (numbers.some((n) => !Number.isInteger(n))) return null;
  if (!/^[0-9a-f]{16}$/.test(rootSeed)) return null;
  return {
    identity: {
      campaignId,
      generatorVersion: numbers[0],
      stencilLibraryVersion: numbers[1],
      contentVersion: numbers[2],
      levelNumber: numbers[3],
    },
    rootSeed,
  };
}

export function isSameIdentity(a: LevelIdentity, b: LevelIdentity): boolean {
  return (
    a.campaignId === b.campaignId &&
    a.levelNumber === b.levelNumber &&
    a.generatorVersion === b.generatorVersion &&
    a.stencilLibraryVersion === b.stencilLibraryVersion &&
    a.contentVersion === b.contentVersion
  );
}

export function isValidIdentity(value: unknown): value is LevelIdentity {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.campaignId === 'string' &&
    candidate.campaignId.length > 0 &&
    candidate.campaignId.length <= 64 &&
    Number.isInteger(candidate.levelNumber) &&
    (candidate.levelNumber as number) >= FIRST_LEVEL &&
    (candidate.levelNumber as number) <= MAX_LEVEL &&
    Number.isInteger(candidate.generatorVersion) &&
    Number.isInteger(candidate.stencilLibraryVersion) &&
    Number.isInteger(candidate.contentVersion)
  );
}
